import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { OrgContextService } from "./org-context.service";

const JOIN_REQUEST_PATH = "/org/admin?tab=invites";
const CONTENT_APPROVALS_PATH = "/user-dashboard/content-approvals";

/** Approval workflow notifications (submit / approve / reject / publish / supersede / promote to public). */
const CONTENT_WORKFLOW_TYPES = new Set([
  "ContentCreated",
  "ContentSubmitted",
  "ContentApproved",
  "ContentRejected",
  "ContentPublished",
  "ContentSuperseded",
  "ContentPromoteRequested",
  "ContentPromoted",
  "ContentPromoteCancelled",
  "ContentReviewed",
  "ContentIssueReported",
  "ContentFeedbackResponded",
  "ContentDraftPending",
  "ContentPublishedRestored",
  "PackGuideUnavailable",
  "ChecklistPendingApproval",
  "ChecklistApproved",
  "ReviewReminder",
]);
const REVIEW_REQUEST_TYPES = new Set([
  "ContentSubmitted",
  "ContentPromoteRequested",
  "ReviewReminder",
]);

/** Org structure notifications sent to Org / App / Team admins. */
const ORG_ADMIN_EVENT_PATHS: Record<string, string> = {
  OrgApplicationCreated: "/org/admin?tab=structure",
  OrgTeamCreated: "/org/admin?tab=structure",
  OrgAdminInviteSent: "/org/admin?tab=invites",
  OrgInviteAccepted: "/org/admin?tab=people",
};

/**
 * Click handling for organization notifications (invites / join requests / decisions,
 * admin structure events and content approval workflow).
 * Opens the related page only when the user has access, switching the
 * account type to that organization first when needed.
 */
@Injectable({ providedIn: "root" })
export class OrgNotificationService {
  constructor(
    private orgContext: OrgContextService,
    private router: Router,
    private toastr: ToastrService
  ) {}

  isOrgNotification(notification: any): boolean {
    const contentType = String(notification?.ContentType || "");
    return contentType.startsWith("Org") || CONTENT_WORKFLOW_TYPES.has(contentType);
  }

  /** Returns true when the notification was an org notification and has been handled. */
  open(notification: any): boolean {
    // Account notifications (trial reminders, failed payments) share this click handler.
    const accountType = String(notification?.ContentType || notification?.contentType || "");
    if (accountType === "TrialReminder") {
      this.router.navigateByUrl(this.navigationUrl(notification) || "/pricing?trial=reminder");
      return true;
    }
    if (accountType === "SubscriptionPaymentFailed") {
      this.router.navigateByUrl(this.navigationUrl(notification) || "/subscriptions?plan=starter&billing=monthly");
      return true;
    }

    if (!this.isOrgNotification(notification)) return false;

    const contentType = String(notification.ContentType || "");
    const orgId =
      notification.OrganizationId ||
      notification.organizationId ||
      this.orgContext.snapshot.activeOrganization?.organizationId ||
      null;

    // Org invites are for users who are not members yet — open join on public
    // account type (do not require switching into the organization).
    if (contentType === "OrgInvite") {
      const inviteId = notification.ContentId || notification.contentId || "";
      const params = new URLSearchParams();
      if (inviteId) params.set("inviteId", inviteId);
      if (orgId) params.set("organizationId", orgId);
      const qs = params.toString();
      const path = qs ? `/org/join?${qs}` : "/org/join";
      if (this.orgContext.isOrganizationMode()) {
        this.orgContext.enterPublicMode().subscribe({
          next: () => window.location.assign(path),
          error: () => this.router.navigateByUrl(path),
        });
      } else {
        this.router.navigateByUrl(path);
      }
      return true;
    }

    const navigationUrl = this.navigationUrl(notification);

    // The removed member has nothing left to open in that organization.
    if (contentType === "OrgMemberDiscontinued" && !navigationUrl) {
      this.toastr.info(
        notification.Message ||
          "Your membership in this organization was discontinued."
      );
      return true;
    }

    // Content notifications for guides outside an organization link straight to the page.
    if (CONTENT_WORKFLOW_TYPES.has(contentType) && !notification.OrganizationId && !notification.organizationId) {
      this.router.navigateByUrl(
        navigationUrl || (REVIEW_REQUEST_TYPES.has(contentType) ? CONTENT_APPROVALS_PATH : "/")
      );
      return true;
    }

    const target =
      contentType === "OrgRoleChanged"
        ? null
        : this.resolveTarget(notification, orgId);
    if (target) {
      this.openInOrganization(orgId, target);
      return true;
    }

    // Memberships may be stale (request accepted / role changed while signed in) — refresh once.
    this.orgContext.load().subscribe(() => {
      const retry = this.resolveTarget(notification, orgId);
      if (retry) {
        this.openInOrganization(orgId, retry);
      } else {
        this.toastr.info(this.noAccessMessage(notification));
      }
    });
    return true;
  }

  private resolveTarget(notification: any, orgId: string | null): string | null {
    if (!orgId) return null;
    const contentType = String(notification.ContentType || "");

    if (CONTENT_WORKFLOW_TYPES.has(contentType)) {
      if (!this.orgContext.isActiveMemberOf(orgId)) return null;
      const url = this.navigationUrl(notification);
      if (url) return url;
      return REVIEW_REQUEST_TYPES.has(contentType) ? CONTENT_APPROVALS_PATH : "/";
    }

    if (ORG_ADMIN_EVENT_PATHS[contentType]) {
      return this.orgContext.canReviewJoinRequests(orgId)
        ? ORG_ADMIN_EVENT_PATHS[contentType]
        : null;
    }

    switch (contentType) {
      case "OrgJoinRequest":
        return this.orgContext.canReviewJoinRequests(orgId)
          ? JOIN_REQUEST_PATH
          : null;
      case "OrgJoinDecision":
      case "OrgRoleChanged":
      case "OrgMemberDiscontinued":
      case "OrgContentReassigned":
      case "OrgApplicationUpdated":
      case "OrgApplicationDeleted":
      case "OrgTeamUpdated":
      case "OrgTeamDeleted":
      case "OrgSettingsChanged": {
        const url = this.navigationUrl(notification);
        if (url?.startsWith("/org/admin")) {
          return this.orgContext.canReviewJoinRequests(orgId) ? url : null;
        }
        if (!this.orgContext.isActiveMemberOf(orgId)) return null;
        return url || "/";
      }
      default:
        return null;
    }
  }

  private navigationUrl(notification: any): string | null {
    const url = String(notification?.NavigationUrl || notification?.navigationUrl || "").trim();
    return url.startsWith("/") ? url : null;
  }

  private noAccessMessage(notification: any): string {
    const contentType = String(notification.ContentType || "");
    if (contentType === "OrgJoinRequest")
      return "You no longer have access to review join requests for this organization.";
    if (ORG_ADMIN_EVENT_PATHS[contentType])
      return "You no longer have admin access to this organization.";
    return "You don't have access to this organization.";
  }

  private openInOrganization(orgId: string | null, path: string): void {
    const activeOrgId = this.orgContext.isOrganizationMode()
      ? this.orgContext.snapshot.activeOrganization?.organizationId ||
        localStorage.getItem("activeOrganizationId")
      : null;
    if (!orgId || activeOrgId === orgId) {
      this.router.navigateByUrl(path);
      return;
    }
    // Hard navigation so the shell and pages remount under the new account type.
    this.orgContext.enterOrganizationMode(orgId).subscribe({
      next: () => window.location.assign(path),
      error: () =>
        this.toastr.error("Could not switch to this organization."),
    });
  }
}
